import { useEffect, useState } from 'react';
import { StreamChat } from 'stream-chat';
import {
  Chat,
  Channel,
  ChannelList,
  LoadingIndicator,
  useChatContext,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/index.css';

import './AdminChatMonitorPage.scss';
import '../../../components/CommonComponents/DashboardChat/DashboardChat.scss';
import '../../../components/CommonComponents/DashboardChat/DashboardChatSidebar.scss';

import { ServerConfig } from '../../../services/NetworkClient/ServerConfig';
import { AdminAPIHandler } from '../../../services/NetworkClient/BackendAPIs/AdminAPIHandler';
import { ErrorLogger } from '../../../utils/Logger';

import { CustomChannelPreview } from '../../../chatModule/CustomChannelPreview/CustomChannelPreview';
import MonitorChannelView from './MonitorChannelView';
import logoImageGrey from '../../../assets/images/branding/gg_navbar_brand_grey.png';

const filters = { type: 'messaging' };
const sort = { last_message_at: -1 } as const;
const options = { limit: 30, watch: false };

// Direct channel lookup by channelId ({gigId}_{buyerId}). Watches the channel
// and sets it as the active channel even if it is not in the queried list.
const ChannelLookup = ({ onActiveChange }: { onActiveChange: (id: string) => void }) => {
  const { client, setActiveChannel } = useChatContext();
  const [channelId, setChannelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const openChannel = async () => {
    const id = channelId.trim();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const channel = client.channel('messaging', id);
      await channel.watch();
      setActiveChannel(channel);
      onActiveChange(id);
    } catch (err) {
      ErrorLogger.log(err, 'AdminChatMonitorPage.openChannel');
      setError('Channel not found or inaccessible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-chat-lookup">
      <input
        className="admin-chat-lookup-input"
        placeholder="Enter channelId e.g. gig123_buyer456"
        value={channelId}
        onChange={(e) => setChannelId(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') openChannel(); }}
      />
      <button className="admin-chat-lookup-btn" onClick={openChannel} disabled={loading}>
        {loading ? '...' : 'Open'}
      </button>
      {error && <div className="admin-chat-lookup-error">{error}</div>}
    </div>
  );
};

const AdminChatMonitorPage = () => {
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [error, setError] = useState('');
  const [activeChannelId, setActiveChannelId] = useState<string | undefined>(undefined);

  useEffect(() => {
    document.title = 'GenieGig | Admin Chat Monitor';
  }, []);

  useEffect(() => {
    let client: StreamChat | null = null;
    let isMounted = true;

    const connectAdmin = async () => {
      try {
        const { token, userId } = await AdminAPIHandler.shared.getStreamChatAdminToken();
        // Dedicated client instance so admin monitoring never collides with a
        // logged-in buyer's shared Stream Chat session.
        client = new StreamChat(ServerConfig.shared.streamChatApiKey);
        await client.connectUser({ id: userId }, token);
        if (isMounted) {
          setChatClient(client);
        } else {
          client.disconnectUser();
        }
      } catch (err) {
        ErrorLogger.log(err, 'AdminChatMonitorPage.connectAdmin');
        if (isMounted) setError('Failed to connect Stream Chat admin client.');
      }
    };

    connectAdmin();

    return () => {
      isMounted = false;
      if (client) client.disconnectUser();
    };
  }, []);

  if (error) {
    return <div className="admin-chat-monitor-error">{error}</div>;
  }

  if (!chatClient) {
    return <LoadingIndicator />;
  }

  return (
    <div className="chatWrapper admin-chat-monitor">
      <div className="admin-monitor-banner">
        <span className="admin-monitor-banner-dot" />
        <span className="admin-monitor-banner-title">Monitor Mode</span>
        <span className="admin-monitor-banner-sub">Read-only · admins observe, never interfere</span>
      </div>
      <Chat client={chatClient}>
        <div className="chatLayout">
          <div className="chatSidebar">
            <div className="admin-monitor-sidebar-title">Conversations</div>
            <ChannelLookup onActiveChange={setActiveChannelId} />
            <ChannelList
              filters={filters}
              sort={sort}
              options={options}
              customActiveChannel={activeChannelId}
              Preview={(props) => (
                <CustomChannelPreview {...props} onChannelSelect={setActiveChannelId} />
              )}
            />
          </div>

          {activeChannelId ? (
            <div className="chatMain">
              <Channel>
                <MonitorChannelView />
              </Channel>
            </div>
          ) : (
            <div className="chatMain chatMain-empty">
              <div className="chat-empty-state">
                <img src={logoImageGrey} alt="GenieGig" className="chat-empty-logo" />
                <p>Select a channel or enter a channelId to monitor a conversation.</p>
              </div>
            </div>
          )}
        </div>
      </Chat>
    </div>
  );
};

export default AdminChatMonitorPage;
